import React, {useEffect, useState} from 'react';
import getTenantCurrency from '../../Actions/getTenantCurrencies';
import TextInput from '../../UI/Form/TextInput';
import TextArea from '../../UI/Form/TextArea';
import BooleanField from '../../UI/Form/BooleanField';
import ImageSelect from '../../UI/Form/ImageSelect';
import SelectDropDown from '../../UI/Form/SelectDropDown';
import {useLocale} from '../../Localization/LocaleContext';

const SubscriptionForm = ({form_state, onUpdateForm}) => {
  const {translate: t} = useLocale();
  const [currencies, setCurrencies] = useState([]);

  useEffect(() => {
    loadCurrency();
  }, []);

  //loadCurrency
  const loadCurrency = async () => {
    const currency = await getTenantCurrency();
    setCurrencies(currency);
  };

  const subscription_type = [
    {
      label: 'Stripe',
      value: 'stripe',
    },
    {
      label: 'Mangopay',
      value: 'mangopay',
    },
    {
      label: 'Android',
      value: 'android',
    },
    {
      label: 'iOS',
      value: 'ios',
    },
    {
      label: t('subscriptions.type.free', 'Free Subscription'),
      value: 'free_subscription',
    },
  ];

  useEffect(() => {
    if (currencies?.length > 0 && form_state?.currency_id == null) {
      onUpdateForm({key: 'currency_id', value: currencies[0].id});
    }
  }, [currencies, form_state?.currency_id, onUpdateForm]);

  return (
    <div className=" max-h-[85dvh] h-full overflow-y-auto border border-gray-200 rounded-md py-4 px-5 w-[97%] m-3 flex flex-col gap-3  ">
      {/* Image */}
      <ImageSelect
        title={t('common.image', 'Image')}
        current_data={form_state?.image_path}
        update_data={value => {
          onUpdateForm({key: 'image_path', value: value});
        }}
      />
      {/* Title */}
      <TextInput
        title={t('common.title', 'Title')}
        current_data={form_state?.title}
        placeholder={t('subscriptions.form.title_placeholder', 'Subscription Title')}
        type={'text'}
        update_data={data => onUpdateForm({key: 'title', value: data})}
        isRequired={true}
        tooltip={
          t('subscriptions.form.title_help', 'Enter a descriptive title for the subscription package (e.g., “Premium Plan”, “Basic Tier”).')
        }
      />
      {/* Description */}
      <TextArea
        title={t('common.description', 'Description')}
        current_data={form_state?.description}
        placeholder={t('subscriptions.form.description_placeholder', 'Subscription Description')}
        type={'text'}
        update_data={data => onUpdateForm({key: 'description', value: data})}
        tooltip={
          t('subscriptions.form.description_help', 'Provide a detailed description of the subscription, outlining its features and benefits.')
        }
      />
      {/* Amount & curency */}
      <div className="flex items-center justify-start gap-2">
        <div className="max-w-[200px] w-full">
          <SelectDropDown
            name={t('common.currency', 'Currency')}
            title={t('common.currency', 'Currency')}
            selected_value={form_state?.currency_id}
            setValue={value => onUpdateForm({key: 'currency_id', value: value})}
            list={currencies?.map(cr => ({label: cr.name, value: cr.id}))}
            default_text={t('subscriptions.form.select_currency', 'Select Currency')}
            isRequired={true}
            tooltip={
              t('subscriptions.form.currency_help', 'Select the currency for the subscription from the dropdown menu (Your default currency)')
            }
          />
        </div>

        <TextInput
          title={t('common.amount', 'Amount')}
          current_data={form_state?.amount}
          placeholder={t('subscriptions.form.amount_placeholder', 'Subscription Amount')}
          type={'number'}
          update_data={data => onUpdateForm({key: 'amount', value: data})}
          tooltip={t('subscriptions.form.amount_help', 'Enter the subscription price.')}
        />
      </div>
      {/* SKU */}
      <TextInput
        title={t('common.sku', 'SKU')}
        current_data={form_state?.sku}
        placeholder={t('subscriptions.form.sku_placeholder', 'Subscription SKU')}
        type={'text'}
        update_data={data => onUpdateForm({key: 'sku', value: data})}
        tooltip={
          t('subscriptions.form.sku_help', 'Enter a unique identifier for the subscription (often used for data simplicity, uniqueness and tracking).')
        }
      />
      {/* allowed_listings */}
      <TextInput
        title={t('subscriptions.form.allowed_listings', 'Allowed Listings')}
        current_data={form_state?.allowed_listings}
        placeholder={t('subscriptions.form.allowed_listings_placeholder', 'Subscription Allowed Listings')}
        type={'number'}
        update_data={data =>
          onUpdateForm({key: 'allowed_listings', value: data})
        }
        isRequired={true}
        tooltip={
          t('subscriptions.form.allowed_listings_help', 'Specify the number of listings allowed for this subscription.')
        }
      />
      {/* fee_fixed_per_order */}
      <TextInput
        title={t('subscriptions.form.fixed_fee_per_order', 'Fixed Fee Per Order')}
        current_data={form_state?.fee_fixed_per_order}
        placeholder={t('subscriptions.form.fixed_fee_placeholder', 'Subscription Fee Fixed Per Order')}
        type={'number'}
        update_data={data =>
          onUpdateForm({key: 'fee_fixed_per_order', value: data})
        }
        tooltip={t('subscriptions.form.fixed_fee_help', 'Enter a fixed fee per order, if applicable.')}
      />
      {/* commission_percent_per_order */}
      <TextInput
        title={t('subscriptions.form.commission_percent_per_order', 'Commission % Per Order')}
        current_data={form_state?.commission_percent_per_order}
        placeholder={t('subscriptions.form.commission_placeholder', 'Subscription Commission Percent Per Order')}
        type={'number'}
        update_data={data =>
          onUpdateForm({key: 'commission_percent_per_order', value: data})
        }
        tooltip={t('subscriptions.form.commission_help', 'Enter the commission percentage per order, if applicable.')}
      />

      {/* expiry_days */}
      <TextInput
        title={t('subscriptions.form.expiry_days', 'Expiry Days')}
        current_data={form_state?.expiry_days}
        placeholder={t('subscriptions.form.expiry_days_placeholder', 'Subscription Expiry Days')}
        type={'number'}
        update_data={data => onUpdateForm({key: 'expiry_days', value: data})}
        isRequired={true}
        tooltip={
          t('subscriptions.form.expiry_days_help', 'Set the number of days before the subscription(plan,package) expires. Example: 7 days, 30 days')
        }
      />
      {/* type */}
      <SelectDropDown
        name={t('common.type', 'Type')}
        title={t('subscriptions.form.payment_method', 'Payment Method')}
        selected_value={form_state?.type}
        setValue={value => onUpdateForm({key: 'type', value: value})}
        list={subscription_type}
        default_text={t('subscriptions.form.select_type', 'Select Type')}
        isRequired={true}
        tooltip={
          t('subscriptions.form.payment_method_help', 'Select the payment method(s) supported for this subscription (e.g., Stripe, Mangopay, Android, iOS, Free Subscription).')
        }
      />

      {/* active */}
      <BooleanField
        title={t('common.active', 'Active')}
        current_data={form_state?.active ?? true}
        placeholder={t('subscriptions.form.active_placeholder', 'Subscription Active')}
        type={'text'}
        update_data={data => {
          onUpdateForm({key: 'active', value: data});
        }}
        tooltip={
          t('subscriptions.form.active_help', 'Toggle the switch to activate or deactivate the subscription. An active subscription is available for purchase.')
        }
      />
    </div>
  );
};

export default SubscriptionForm;
